import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import { Box, getCSSVariable, Link, PressableBox, Row, Text } from '@opendoor/bricks-next';

import { EiChevronDownIcon } from '../../../shared/EiChevronDownIcon';
import type { MegaNavItem, MenuLink } from '../types';
import MegaNavFlyout from './MegaNavFlyout';

interface MegaNavProps {
  slug: string;
  mainNavLinks: MenuLink[];
  megaNavData?: MegaNavItem[];
}

export const MegaNav: React.FC<MegaNavProps> = ({ slug, megaNavData }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const [flyoutTopOffset, setFlyoutTopOffset] = useState(72);
  const navRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (itemName: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Calculate the flyout top position based on where the nav actually is in the viewport
    // This accounts for any banners or other elements above the header
    if (navRef.current) {
      const rect = navRef.current.getBoundingClientRect();
      setFlyoutTopOffset(rect.bottom);
    }
    setOpenNavItem(itemName);
  };

  const handleMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenNavItem(null);
      closeTimerRef.current = null;
    }, 400);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavItem(null);
      }
    };

    if (openNavItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavItem]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <Box
      display="block"
      ref={navRef}
      position="relative"
      $platform-web={{ gridColumn: '1 / -1', gridRow: '1 / 2' }}
    >
      <Row
        m={0}
        tag="ul"
        $smallerThanMD={{ display: 'none' }}
        justifyContent="center"
        analyticsName="cosmos-mega-nav-menu-list"
        css={css`
          list-style: none;
        `}
      >
        {megaNavData?.map((navItem) => {
          const isOpen = openNavItem === navItem.name;
          const hasSections = navItem.sections && navItem.sections.length > 0;

          return (
            <Box
              tag="li"
              display="block"
              key={navItem.name}
              position="relative"
              {...(hasSections && {
                onMouseEnter: () => handleMouseEnter(navItem.name),
                onMouseLeave: handleMouseLeave,
              })}
            >
              {hasSections ? (
                <>
                  <MegaNavLink
                    active={slug === navItem.href}
                    href={navItem.href}
                    name={navItem.name}
                    isOpen={isOpen}
                  >
                    <Text tag="span" typography="$labelMedium">
                      {navItem.name}
                    </Text>
                  </MegaNavLink>
                  <MegaNavFlyout
                    item={navItem}
                    isVisible={isOpen}
                    onMouseEnter={() => handleMouseEnter(navItem.name)}
                    onMouseLeave={handleMouseLeave}
                    topOffset={flyoutTopOffset}
                  />
                </>
              ) : (
                <Link
                  analyticsName={`cosmos-landing-page-v2-nav-link-${navItem.name}`}
                  aria-label="View link"
                  href={navItem.href}
                  aria-current={slug === navItem.href}
                  color="$contentPrimary"
                  typography="$labelMedium"
                  alignItems="center"
                  py="$2"
                  px="$4"
                  display="inline-flex"
                  height="100%"
                >
                  <Text tag="span" typography="$labelMedium" color="$contentPrimary">
                    {navItem.name}
                  </Text>
                </Link>
              )}
            </Box>
          );
        })}
      </Row>
    </Box>
  );
};

const MegaNavLink: React.FC<
  PropsWithChildren<{
    href: string;
    active?: boolean;
    name: string;
    isOpen: boolean;
  }>
> = ({ children, name, active, isOpen, href }) => (
  <PressableBox
    href={href}
    display="inline-flex"
    flexDirection="row"
    alignItems="center"
    py="$4x"
    px="$8x"
    height={72}
    aria-label="View link"
    aria-current={active}
    aria-expanded={isOpen}
    analyticsName={`cosmos-mega-nav-v2-nav-link-${name}`}
    css={css`
      background: transparent;
      border: none;
      cursor: pointer;
      color: ${getCSSVariable('$contentPrimary', 'color')};
      text-decoration: none;
      gap: 8px;
      line-height: 100%;
      font-weight: 500;

      &:hover,
      &:focus {
        color: ${getCSSVariable('$contentAccentBrand', 'color')};
        outline: 0;
      }
      &:active {
        color: ${getCSSVariable('$contentAccentBrand', 'color')};
      }
    `}
  >
    {children}
    <Row
      alignItems="center"
      justifyContent="center"
      css={css`
        width: 0.6em;
        height: 0.6em;
        transition: transform 0.2s ease;
        transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      `}
    >
      <EiChevronDownIcon />
    </Row>
  </PressableBox>
);
